import { yupResolver } from "@hookform/resolvers/yup";
import useIsMounted from "ismounted";
import { filter } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import {
  Controller,
  FieldError,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { reach } from "yup";
import { Select } from "../../../../components/forms/select";
import { Permission } from "../../../../components/permission";
import { StepperContext } from "../../../../components/stepper/context";
import { LaunchConfigurationsDTO } from "../../../../store/launch-configurations/models/dto";
import { isDefined } from "../../../../utils/utils";
import { Container, FieldSection, SectionTitle } from "../common/components";
import MultiTextField from "../common/multi-lingual-text";
import { useDebouncedFormChange } from "../hooks";
import { Between } from "./between";
import { CryptoOrStandard } from "./crypto-or-standard";

type ParentFormType = LaunchConfigurationsDTO.Requests.SetSingle.T;
type InnerFormType = {
  supportedFormLanguages: ParentFormType["configuration"]["supportedFormLanguages"];
  launchName: ParentFormType["configuration"]["launchName"];
  formTitle: ParentFormType["configuration"]["formTitle"];
  header: ParentFormType["configuration"]["header"];
  welcomeMessage: ParentFormType["configuration"]["welcomeMessage"];
  between: ParentFormType["configuration"]["between"];
};

export const Configuration: React.FunctionComponent<{}> = () => {
  const { setValidation } = useContext(StepperContext);
  const isMounted = useIsMounted();

  const schema = LaunchConfigurationsDTO.Requests.SetSingle.useSchema();
  const resolver = useMemo(
    () =>
      reach(schema, "configuration").pick([
        "supportedFormLanguages",
        "launchName",
        "formTitle",
        "header",
        "welcomeMessage",
        "between",
      ]),
    [schema]
  );

  const {
    setValue,
    getValues,
  } = useFormContext<LaunchConfigurationsDTO.Requests.SetSingle.T>();

  const innerForm = useForm<InnerFormType>({
    resolver: yupResolver(resolver),
    defaultValues: useMemo(() => getValues().configuration, [getValues]),
  });

  const setOuterValues = useCallback(
    (x) => {
      setValue("configuration.launchName", x.launchName as any);
      setValue("configuration.formTitle", x.formTitle as any);
      setValue("configuration.header", x.header as any);
      setValue("configuration.welcomeMessage", x.welcomeMessage as any);
      setValue("configuration.between", x.between as any);
    },
    [setValue]
  );

  useDebouncedFormChange(innerForm, setOuterValues);

  useEffect(() => {
    const sub = innerForm.watch((d) => {
      setValue(
        "configuration.supportedFormLanguages",
        filter(d.supportedFormLanguages || [], isDefined)
      );
    });

    return () => sub.unsubscribe();
  }, [innerForm.watch, setValue]);

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        innerForm.handleSubmit((d) => {
          setOuterValues(d);
          res();
        })();
      })
    );
    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    setValidation,
    innerForm.handleSubmit,
    isMounted.current,
    setOuterValues,
  ]);

  return (
    <Container>
      <FormProvider {...innerForm}>
        <Permission roles={["ROLE_CRYPTO"]}>
          <FieldSection>
            <CryptoOrStandard />
          </FieldSection>
        </Permission>
        <FieldSection>
          <SectionTitle>Supported Languages</SectionTitle>
          <Controller
            control={innerForm.control}
            name="supportedFormLanguages"
            render={({ field, fieldState: { error } }) => (
              <Select
                fullWidth
                multiple
                options={[
                  {
                    title: "English",
                    value: "en_GB",
                  },
                  {
                    title: "Spanish",
                    value: "es_ES",
                  },
                ]}
                label="Selected Languages"
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FieldSection>
        <FieldSection>
          <SectionTitle>Launch Name</SectionTitle>
          <Controller
            control={innerForm.control}
            name="launchName"
            defaultValue={{}}
            render={({ field, fieldState: { error } }) => (
              <MultiTextField
                {...field}
                error={(error as any) as Record<string, FieldError> | undefined}
              />
            )}
          />
        </FieldSection>
        <FieldSection>
          <SectionTitle>Form Title</SectionTitle>
          <Controller
            control={innerForm.control}
            name="formTitle"
            defaultValue={{}}
            render={({ field, fieldState: { error } }) => (
              <MultiTextField
                {...field}
                error={(error as any) as Record<string, FieldError> | undefined}
              />
            )}
          />
        </FieldSection>

        <FieldSection>
          <SectionTitle>Header</SectionTitle>
          <Controller
            control={innerForm.control}
            name="header.text"
            defaultValue={{}}
            render={({ field, fieldState: { error } }) => (
              <MultiTextField
                {...field}
                error={(error as any) as Record<string, FieldError> | undefined}
              />
            )}
          />
        </FieldSection>
        <FieldSection>
          <SectionTitle>Welcome Message</SectionTitle>
          <Controller
            control={innerForm.control}
            name="welcomeMessage"
            defaultValue={{}}
            render={({ field, fieldState: { error } }) => (
              <MultiTextField
                {...field}
                value={field.value || {}}
                error={(error as any) as Record<string, FieldError> | undefined}
              />
            )}
          />
        </FieldSection>
        <FieldSection>
          <SectionTitle>Date and Time</SectionTitle>
          <Between />
        </FieldSection>
      </FormProvider>
    </Container>
  );
};
